import { Injectable, NgZone, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { eventIdOf } from './event-id';
import { isManagerPreviewTab, JwtService } from './jwt.service';
import { UserService } from './user.service';

const BATCH_SIZE = 30;
const SEND_AFTER_MS = 30 * 1000;
// Pracę ratowników admin podgląda na żywo (Statystyki → Na żywo), więc ich paczki idą częściej.
const PARAMEDIC_SEND_AFTER_MS = 5 * 1000;
const PATH_LENGTH = 191;
const PAUSE_AFTER_FAILURE_MS = 5 * 60 * 1000;
const SEND_PENDING_WAIT_MS = 2 * 1000;

interface UsageEvent {
  id: string;
  t: number;
  path?: string;
}

@Injectable({ providedIn: 'root' })
export class UsageTrackerService {

  private events: UsageEvent[] = [];
  private batchToken: string;
  private clickedLabel: HTMLLabelElement;
  private sendTimer;
  private pausedUntil = 0;
  private failed = false;

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private jwtService: JwtService,
    @Optional() private userService?: UserService
  ) { }

  start(): void {
    if (window.localStorage['usageTracking'] === 'off') {
      return;
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.runSafely(() => this.onNavigation());
      }
    });
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('click', event => this.runSafely(() => this.onClick(event)), true);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.runSafely(() => this.send());
        }
      });
      window.addEventListener('pagehide', () => this.runSafely(() => this.send()));
    });
  }

  sendPending(): Promise<void> {
    let sending: Promise<void>;
    this.runSafely(() => sending = this.send());
    return new Promise(resolve => {
      setTimeout(resolve, SEND_PENDING_WAIT_MS);
      Promise.resolve(sending).then(resolve, resolve);
    });
  }

  private runSafely(action: () => void): void {
    if (this.failed) {
      return;
    }
    try {
      this.ngZone.runOutsideAngular(action);
    } catch (error) {
      this.failed = true;
      console.error('Usage tracking stopped after an error', error);
    }
  }

  private onNavigation(): void {
    const id = screenEventId(this.router.routerState.snapshot.root);
    if (id && this.isCollecting()) {
      this.collect(id, screenPath(this.router.url));
    }
  }

  private onClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const label = target.closest('label');
    const isEchoOfLabelClick = !!this.clickedLabel && this.clickedLabel.control === target;
    this.clickedLabel = label && label.control !== target ? label : undefined;
    if (!isEchoOfLabelClick && this.isCollecting()) {
      const id = eventIdOf(target);
      if (id) {
        this.collect(id);
      }
    }
  }

  private isCollecting(): boolean {
    return Date.now() >= this.pausedUntil && !!this.jwtService.getToken()
      && window.localStorage.getItem('impersonateActive') !== '1' && !isManagerPreviewTab();
  }

  private collect(id: string, path?: string): void {
    if (this.events.length >= BATCH_SIZE) {
      this.send();
    }
    this.events.push(path ? { id, t: Date.now(), path } : { id, t: Date.now() });
    if (this.events.length === 1) {
      this.batchToken = this.jwtService.getToken();
      this.sendTimer = setTimeout(() => this.runSafely(() => this.send()), this.isParamedic() ? PARAMEDIC_SEND_AFTER_MS : SEND_AFTER_MS);
    }
  }

  private isParamedic(): boolean {
    const user = this.userService && this.userService.getCurrentUser();
    return !!user && user.role === 'ROLE_API_CAREGIVER' && !!user.user_id && user.user_id.type === 'Paramedic';
  }

  private send(): Promise<void> {
    const events = this.events;
    this.events = [];
    clearTimeout(this.sendTimer);
    if (!events.length || this.jwtService.getToken() !== this.batchToken) {
      return Promise.resolve();
    }

    return fetch(environment.api_url + '/web-events', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', 'X-Access-Token': this.batchToken },
      body: JSON.stringify({ sentAt: Date.now(), events })
    }).then(response => {
      if (!response.ok) {
        this.pause(response.status);
      }
    }, error => this.pause(error));
  }

  private pause(reason: any): void {
    console.warn('Usage tracking paused after a failed send', reason);
    this.pausedUntil = Date.now() + PAUSE_AFTER_FAILURE_MS;
    this.events = [];
    clearTimeout(this.sendTimer);
  }
}

// Adres z parametrami trasy (np. IMEI pacjenta), bez query stringa.
function screenPath(url: string): string {
  return url ? url.split(/[?#]/)[0].slice(0, PATH_LENGTH) : undefined;
}

function screenEventId(root: ActivatedRouteSnapshot): string {
  let route = root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  return route.routeConfig && route.routeConfig.data && route.routeConfig.data.event;
}
